/* eslint-disable react/no-danger */
import { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Stack,
  Group,
  Text,
  Paper,
  Badge,
  Button,
  Loader,
  Alert,
  Timeline,
  Divider,
} from "@mantine/core";
import {
  IconHistory,
  IconCheck,
  IconFileText,
  IconAlertCircle,
  IconClock,
  IconPencil,
  IconPlus,
  IconEdit,
  IconDownload,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import {
  getSignOffHistory,
  downloadReportPdf,
  SignOffHistoryItem,
} from "../../services/signOff.service";
import { resolveVariableChipValues } from "../shared/tiptap/VariableChip";

function resolveCommentsHtml(comments: string): string {
  if (!comments) return comments;
  let result = resolveVariableChipValues(comments);
  result = result.replace(
    /<span[^>]*data-variable="([^"]+)"[^>]*>.*?<\/span>/g,
    (_m, varName: string) => varName
  );
  result = result.replace(/\{\{(\w+)\}\}/g, (_m, varName: string) => varName);
  return result;
}

interface ReportHistoryModalProps {
  opened: boolean;
  onClose: () => void;
  caseId: string;
  caseNumber: string;
  onCreateAmendment?: () => void;
}

function getAmendmentTypeLabel(type: string): {
  label: string;
  color: string;
  icon: React.ReactNode;
} {
  switch (type) {
    case "initial":
      return {
        label: "Original Sign-Off",
        color: "green",
        icon: <IconCheck size={14} />,
      };
    case "corrected":
      return {
        label: "CORRECTED",
        color: "blue",
        icon: <IconEdit size={14} />,
      };
    case "addendum":
      return {
        label: "ADDENDUM",
        color: "cyan",
        icon: <IconPlus size={14} />,
      };
    case "amended":
      return {
        label: "AMENDED",
        color: "orange",
        icon: <IconPencil size={14} />,
      };
    default:
      return {
        label: type.toUpperCase(),
        color: "gray",
        icon: <IconFileText size={14} />,
      };
  }
}

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function ReportHistoryModal({
  opened,
  onClose,
  caseId,
  caseNumber,
  onCreateAmendment,
}: ReportHistoryModalProps) {
  const [history, setHistory] = useState<SignOffHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownloadLatest = async () => {
    setDownloading(true);
    try {
      await downloadReportPdf(caseId, caseNumber);
    } catch (err: any) {
      notifications.show({
        color: "red",
        title: "Download failed",
        message: err?.message || "Could not download the latest report.",
      });
    } finally {
      setDownloading(false);
    }
  };

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getSignOffHistory(caseId);
      // Sort by version (ascending) to show timeline from oldest to newest
      setHistory(data.sort((a, b) => a.reportVersion - b.reportVersion));
    } catch (err: any) {
      setError(err.message || "Failed to load report history");
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    if (opened && caseId) {
      loadHistory();
    }
  }, [opened, caseId, loadHistory]);

  const originalReport = history.find((h) => h.signOffType === "initial");
  const amendments = history.filter((h) => h.signOffType !== "initial");

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconHistory size={20} />
          <span>Report History - {caseNumber}</span>
        </Group>
      }
      size="xl"
      centered
    >
      <Stack gap="md">
        {loading && (
          <Group justify="center" py="xl">
            <Loader size="md" />
            <Text size="sm" c="dimmed">
              Loading history...
            </Text>
          </Group>
        )}

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
            {error}
          </Alert>
        )}

        {!loading && !error && history.length === 0 && (
          <Alert icon={<IconAlertCircle size={16} />} color="gray">
            No sign-off history found for this case.
          </Alert>
        )}

        {!loading && !error && history.length > 0 && (
          <>
            {/* Original Report Section */}
            {originalReport && (
              <Paper p="md" withBorder radius="md">
                <Group justify="space-between" mb="sm">
                  <Text fw={600} size="sm">
                    Original Report
                  </Text>
                  <Badge
                    color="green"
                    size="sm"
                    leftSection={<IconCheck size={12} />}
                  >
                    Version {originalReport.reportVersion}
                  </Badge>
                </Group>
                <Stack gap="xs">
                  <Group gap="xs">
                    <IconClock size={14} color="var(--mantine-color-dimmed)" />
                    <Text size="sm">
                      <Text span fw={500}>
                        Signed:
                      </Text>{" "}
                      {formatDateTime(originalReport.signedAt)}
                      {originalReport.signedBy && (
                        <>
                          {" "}
                          by{" "}
                          <Text span fw={500}>
                            {originalReport.signedBy.fullName}
                          </Text>
                        </>
                      )}
                    </Text>
                  </Group>
                  {originalReport.comments && (
                    <Text size="sm" c="dimmed">
                      {originalReport.comments}
                    </Text>
                  )}
                </Stack>
              </Paper>
            )}

            {/* Amendments Section */}
            {amendments.length > 0 && (
              <>
                <Divider
                  label={
                    <Group gap="xs">
                      <IconFileText size={14} />
                      <Text size="sm" fw={500}>
                        Amendments ({amendments.length})
                      </Text>
                    </Group>
                  }
                  labelPosition="left"
                />

                <Timeline
                  active={amendments.length}
                  bulletSize={28}
                  lineWidth={2}
                >
                  {amendments.map((amendment) => {
                    const typeInfo = getAmendmentTypeLabel(
                      amendment.signOffType
                    );
                    return (
                      <Timeline.Item
                        key={amendment.id}
                        bullet={typeInfo.icon}
                        title={
                          <Group gap="xs">
                            <Badge color={typeInfo.color} size="sm">
                              {typeInfo.label}
                            </Badge>
                            <Text size="xs" c="dimmed">
                              Version {amendment.reportVersion}
                            </Text>
                          </Group>
                        }
                      >
                        <Paper p="sm" withBorder radius="sm" mt="xs">
                          <Stack gap="xs">
                            <Text size="sm" c="dimmed">
                              {formatDateTime(amendment.signedAt)}
                              {amendment.signedBy && (
                                <>
                                  {" "}
                                  by{" "}
                                  <Text span fw={500} c="dark">
                                    {amendment.signedBy.fullName}
                                  </Text>
                                </>
                              )}
                            </Text>

                            {amendment.amendmentReason && (
                              <div>
                                <Text size="xs" fw={500} c="dimmed" mb={2}>
                                  Reason:
                                </Text>
                                <div
                                  style={{ fontSize: 14, lineHeight: 1.6 }}
                                  dangerouslySetInnerHTML={{
                                    __html: resolveCommentsHtml(
                                      amendment.amendmentReason
                                    ),
                                  }}
                                />
                              </div>
                            )}

                            {amendment.comments && (
                              <div>
                                <Text size="xs" fw={500} c="dimmed" mb={2}>
                                  Content:
                                </Text>
                                <div
                                  style={{ fontSize: 14, lineHeight: 1.6 }}
                                  dangerouslySetInnerHTML={{
                                    __html: resolveCommentsHtml(
                                      amendment.comments
                                    ),
                                  }}
                                />
                              </div>
                            )}
                          </Stack>
                        </Paper>
                      </Timeline.Item>
                    );
                  })}
                </Timeline>
              </>
            )}

            {/* Current Version Summary */}
            <Paper p="sm" bg="gray.0" radius="md">
              <Group justify="space-between">
                <Text size="sm">
                  <Text span fw={500}>
                    Current Version:
                  </Text>{" "}
                  {history.length > 0
                    ? Math.max(...history.map((h) => h.reportVersion))
                    : 1}
                </Text>
                <Text size="sm" c="dimmed">
                  {history.length === 1
                    ? "No amendments"
                    : `${amendments.length} amendment${
                        amendments.length !== 1 ? "s" : ""
                      }`}
                </Text>
              </Group>
            </Paper>
          </>
        )}

        {/* Action Buttons */}
        <Divider />
        <Group justify="space-between">
          <Button variant="subtle" onClick={onClose}>
            Close
          </Button>
          <Group gap="xs">
            {onCreateAmendment && (
              <Button
                variant="light"
                leftSection={<IconPencil size={16} />}
                onClick={() => {
                  onClose();
                  onCreateAmendment();
                }}
              >
                Create Amendment
              </Button>
            )}
            {history.length > 0 && (
              <Button
                variant="light"
                leftSection={<IconDownload size={16} />}
                onClick={handleDownloadLatest}
                loading={downloading}
              >
                Download Latest Report
              </Button>
            )}
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
